// Angular imports
import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';

// Rxjs import
import { Subscription, Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

// 3rd party imports
import moment from 'moment-timezone';

// Component imports
import { PortalAnnouncementComponent } from '../portal-announcement/portal-announcement.component';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { UserService } from '../../_services/data-services/user.service';
import { PrecisionService } from '../../_services/shared-services/precision.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { CiraHelpService } from '../../_services/shared-services/cira-help.service';
import { CustomAnalyticsService } from '../../_services/shared-services/analytics.service';
import { ArchitecturalApplicationService } from '../../accounts-module/_services/data-services/architectural-application.service';
import { AccountComplianceService } from '../../accounts-module/_services/data-services/account-compliance.service';
import { DocumentService } from '../../accounts-module/_services/data-services/document.service';

// Architectural application imports
import { AccParameterDto } from '../../accounts-module/_models/architectural-application-parameter-dto';
import { ViolationParameterDto } from '../../accounts-module/_models/violation-parameter-dto';
import { ViolationHistoryParameterDto } from '../../accounts-module/_models/violation-history-parameter-dto';
import { DocumentParamterDto } from '../../accounts-module/_models/document-parameter-dto';

// Model imports
import { UserDashboardItemResultDto } from '../../_models/user-dashboard-item-result-dto';
import { BaseParameterDto } from '../../_models/base-parameter-dto';
import { NavigationInfo } from '../../_models/navigation-info';
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';
import { BaseResidentParameterDto } from '../../_models/base-resident-parameter-dto';
import { ActionItemConfigResultDto } from '../../_models/action-item-config-result-dto';
import { AnnouncementsParametersDto } from '../../_models/announcements-parameters-dto';
import { AnnouncementsResultDto } from '../../_models/announcements-result-dto';
import { UserDashboardResultDto } from '../../_models/user-dashboard-result-dto';
import { ActivityFeedResultDto } from '../../_models/activity-feed-result-dto';

// Constant imports
import { ValidationFormatConstants } from '../../_constants/validation-format-constants';

// Package.json import
import packageInfo from '../../../../package.json';

//CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';
import { Utils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-user-dashboard',
  templateUrl: './user-dashboard.component.html',
  styleUrls: ['./user-dashboard.component.scss'],
  standalone: false
})
export class UserDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('announcements', { static: false })
  announcements: PortalAnnouncementComponent;

  isSmallScreen = false;
  isLargeScreen = true;
  loadingVisible = false;
  helpLink: string;
  appVersion: string = packageInfo.version;

  dashboardResult: UserDashboardResultDto;
  dashboardItems: Array<UserDashboardItemResultDto>;

  accountBalanceItem: UserDashboardItemResultDto;
  complianceItem: UserDashboardItemResultDto;
  documentsItem: UserDashboardItemResultDto;
  architecturalItem: UserDashboardItemResultDto;
  quickLinksItem: UserDashboardItemResultDto;
  announcementItem: UserDashboardItemResultDto;
  activityItem: UserDashboardItemResultDto;
  emptyItem: UserDashboardItemResultDto;

  isLoadComplete: boolean;
  showUserDashboard: boolean;
  userActions: ActionItemConfigResultDto[] = [];
  announcementCount: number;

  selectedProperty: UserPropertyResultDto;
  userProperties: UserPropertyResultDto[] = [];
  hasMultipleCommunities = false;
  impersonateChangeSubscription: Subscription;
  propertyChangeSubscription: Subscription;
  private cancelDocumentFetch$ = new Subject<void>();
  latestAnnouncement: AnnouncementsResultDto | null = null;
  latestAnnouncementPlainText: string = '';
  isLoadingAnnouncement: boolean = true;

  constructor(
    private screenService: ScreenService,
    private userService: UserService,
    private errorService: ServerErrorService,
    private precisionService: PrecisionService,
    private helperService: HelperService,
    private ciraHelpService: CiraHelpService,
    private analyticsService: CustomAnalyticsService,
    private architecturalApplicationService: ArchitecturalApplicationService,
    private accountComplianceService: AccountComplianceService,
    private documentService: DocumentService
  ) {}

  ngOnInit() {
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );

    this.updateSelectedProperty();
    this.propertyChangeSubscription = RxjsUtils.subscribeToSubject(
      this.userService.getSubjectPropertySelectionChanged(),
      (userProperty) => {
        if (!!userProperty) {
          this.selectedProperty = userProperty;
          this.updateSelectedProperty();
          this.populateDashboardItems(true);
        }
      }
    );

    this.impersonateChangeSubscription = RxjsUtils.subscribeToSubject(
      this.userService.getSubjectImpersonateChanged(),
      (flag) => {
        if (!this.userService.isImpersonated()) {
          this.populateDashboardItems(true);
        } else {
          this.isLoadComplete = false;
          this.populateUserProperties(true, false);
        }
      }
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectNewPropertyAdded(),
      () => {
        this.populateUserProperties(true);
      }
    );
    this.populateUserProperties();
  }

  ngOnDestroy() {
    this.cancelDocumentFetch$.next();
    this.cancelDocumentFetch$.complete();
    if (!!this.propertyChangeSubscription) {
      this.propertyChangeSubscription.unsubscribe();
    }
    if (!!this.impersonateChangeSubscription) {
      this.impersonateChangeSubscription.unsubscribe();
    }
  }

  onDashboardItemClick(e): void {
    if (e.code === 'announcement') {
      this.announcements.show();
    } else if (e.code !== 'announcement') {
      let code: string = e.code.toLowerCase();
      let view: string =
        code === 'account-statement' && e.data && e.data.activityDate
          ? e.data.activityDate.toISOString()
          : null;
      if (
        code.startsWith('compliance') ||
        code.startsWith('architectural-application')
      ) {
        const tokens = code.split('-');
        view =
          ['open', 'closed'].indexOf(tokens[tokens.length - 1]) !== -1
            ? tokens[tokens.length - 1]
            : null;
      }

      if (code.startsWith('compliance')) {
        code = 'compliance';
      } else if (code.startsWith('architectural-application')) {
        code = 'architectural-application';
      }

      this.navigateToLink(code, e.data, view);
    }
  }

  navigateToLink(code: string, data?: any, view?: string): void {
    const info: NavigationInfo = new NavigationInfo();
    if (!data) {
      data = this.selectedProperty;
    }
    info.customerId = data.customerId;
    info.communityId = data.communityId.toString();
    info.navCode = code;
    info.view = view;
    this.trackQuickLinkClick(info);
    this.helperService.navigateToRoute(info);
  }

  private trackQuickLinkClick(info: NavigationInfo): void {
    if (info.navCode === '/accounts/one-time-payment') {
      this.analyticsService.trackEvent('payment_quick_link', {
        community_id: info.communityId
      });
    } else if (info.navCode === '/accounts/set-recurring-payment') {
      this.analyticsService.trackEvent('recurring_quick_link', {
        community_id: info.communityId
      });
    }
  }

  private populateUserProperties(
    clearCache = false,
    loadDashboards = true
  ): void {
    this.hasMultipleCommunities = false;
    RxjsUtils.safeSubscribe(
      this.userService.getUserProperties(clearCache),
      (results) => {
        this.userProperties = results;
        this.hasMultipleCommunities =
          results
            .map((r) => r.communityId)
            .filter((v, i, a) => a.indexOf(v) === i).length > 1;

        // Ensure selected property is set after properties are loaded
        if (!this.selectedProperty) {
          this.selectedProperty = this.userService.getSelectedProperty();
        }

        if (loadDashboards) {
          this.populateDashboardItems(clearCache);
        }
        this.getAnnouncementCount();
      },
      (error) => {
        this.errorService.displayError(error);
        if (loadDashboards) {
          this.populateDashboardItems(clearCache);
        }
        this.getAnnouncementCount();
      }
    );
  }

  private populateDashboardItems(clearCache = false): void {
    this.cancelDocumentFetch$.next();
    this.dashboardItems = [];
    this.showUserDashboard = false;
    this.accountBalanceItem = undefined;
    this.complianceItem = undefined;
    this.documentsItem = undefined;
    this.architecturalItem = undefined;
    this.announcementItem = undefined;
    this.quickLinksItem = undefined;
    this.emptyItem = undefined;
    this.activityItem = undefined;

    // Ensure we have the latest selected property
    if (!this.selectedProperty) {
      this.selectedProperty = this.userService.getSelectedProperty();
    }

    if (!this.userService.isInternal() && !!this.selectedProperty) {
      const param = new BaseParameterDto();
      param.clearCache = clearCache;
      this.loadingVisible = true;
      this.isLoadComplete = false;
      RxjsUtils.safeSubscribe(
        this.userService.getUserDashboardItems(param),
        (result) => {
          if (
            Utils.notNullAndDefined(result) &&
            (Utils.isNullOrEmpty(result.status) ||
              Utils.notEqualsIgnoreCase(result.status, 'Error'))
          ) {
            const dashboardItems: UserDashboardItemResultDto[] = [];
            result.dashboardItems.forEach((item) => {
              item.columnFormat =
                item.valueDataType === 'CURRENCY'
                  ? this.precisionService.getCurrencyFormat()
                  : this.precisionService.getCountFormat();
              item.columnFormat =
                item.valueDataType === 'DATETIME'
                  ? ValidationFormatConstants.dateFormat
                  : item.columnFormat;
              // Store the unpaid date item for calculating days past due
              let unpaidDateItem: UserDashboardItemResultDto | undefined;
              switch (item.code) {
                case 'account-statement':
                  if (item.valueDataType === 'CURRENCY') {
                    // Filter to selected property only
                    if (this.selectedProperty && item.details) {
                      item.details = item.details.filter(
                        (d) =>
                          d.communityId === this.selectedProperty.communityId &&
                          d.customerId === this.selectedProperty.customerId
                      );
                    }
                    // Find the unpaid date item for calculating days past due
                    unpaidDateItem = result.dashboardItems.find(
                      (r) =>
                        r.code === 'account-statement' &&
                        r.valueDataType === 'DATETIME'
                    );
                    if (unpaidDateItem && this.selectedProperty) {
                      unpaidDateItem.details = unpaidDateItem.details?.filter(
                        (d) =>
                          d.communityId === this.selectedProperty.communityId &&
                          d.customerId === this.selectedProperty.customerId
                      );
                    }
                    this.buildAccountBalanceItem(item, unpaidDateItem);
                    this.accountBalanceItem = item;
                  }
                  break;
                case 'compliance':
                  // Filter to selected property only
                  if (this.selectedProperty && item.details) {
                    item.details = item.details.filter(
                      (d) =>
                        d.communityId === this.selectedProperty.communityId &&
                        d.customerId === this.selectedProperty.customerId
                    );
                  }
                  this.buildComplianceItem(item);
                  this.complianceItem = item;
                  dashboardItems.push(item);
                  break;
                case 'architectural-application':
                  // Filter to selected property only
                  if (this.selectedProperty && item.details) {
                    item.details = item.details.filter(
                      (d) =>
                        d.communityId === this.selectedProperty.communityId &&
                        d.customerId === this.selectedProperty.customerId
                    );
                  }
                  // Only show card if there are open requests
                  if (item.details?.length > 0) {
                    this.buildArchitecturalApplicationItem(item);
                    this.architecturalItem = item;
                    dashboardItems.push(item);
                  }
                  break;
              }
            });
            if (
              Utils.notNullAndDefined(this.accountBalanceItem) &&
              Utils.notNullAndDefined(this.accountBalanceItem.status) &&
              Utils.equalsIgnoreCase(this.accountBalanceItem.status, 'OK')
            ) {
              const unpaidItem = result.dashboardItems.find(
                (r) =>
                  r.code === 'account-statement' &&
                  r.valueDataType === 'DATETIME'
              );
              if (!!unpaidItem && unpaidItem.status === 'WARNING') {
                unpaidItem.details
                  .filter((d) => !!d && !!d.value && !!d.value.length)
                  .forEach((d) => (d.activityDate = moment(d.value).toDate()));
                this.accountBalanceItem.childItem = unpaidItem;
              }
            }
            this.dashboardResult = result;
            this.showUserDashboard =
              !!this.selectedProperty &&
              (result.dashboardItems.length > 0 ||
                result.activityItems.length > 0);

            if (this.showUserDashboard) {
              this.buildDocumentsItem();

              this.quickLinksItem = new UserDashboardItemResultDto();
              this.buildQuickLinksItem(this.quickLinksItem);

              // Adding announcement to fill dashboard items
              if (!this.accountBalanceItem) {
                this.announcementItem = new UserDashboardItemResultDto();
                this.buildAnnouncementsItem(this.announcementItem);
              } else if (dashboardItems.length < 2) {
                const item = new UserDashboardItemResultDto();
                this.buildAnnouncementsItem(item);
                dashboardItems.push(item);
              }
              this.dashboardItems = dashboardItems.filter(
                (d) => !!d && !!d.code
              );
              this.buildActivityItems();
              this.addTileEventListeners();
            } else {
              this.emptyItem = new UserDashboardItemResultDto();
              this.buildEmptyItem(this.emptyItem);
              this.emptyItem.html = `Dashboard is not available!`;
            }
          } else {
            this.emptyItem = new UserDashboardItemResultDto();
            this.buildEmptyItem(this.emptyItem);
            this.emptyItem.html = `Dashboard is not available!`;
          }
          this.loadingVisible = false;
          this.isLoadComplete = true;
        },
        (error) => {
          this.loadingVisible = false;
          this.errorService.displayError(error);
          this.emptyItem = new UserDashboardItemResultDto();
          this.buildEmptyItem(this.emptyItem);
          this.emptyItem.html = `Dashboard is not available!`;
        }
      );
    } else {
      // External user with properties but no selection yet: show skeleton
      // while the property selection popup loads
      if (!this.userService.isInternal() && this.userProperties?.length > 0) {
        this.isLoadComplete = false;
      } else {
        this.emptyItem = new UserDashboardItemResultDto();
        this.buildEmptyItem(this.emptyItem);
        this.isLoadComplete = true;
      }
    }
  }

  private updateSelectedProperty(): void {
    this.selectedProperty = this.userService.getSelectedProperty();
    this.updatePaymentAccess(this.selectedProperty);
    this.updateHelpLink(this.selectedProperty);
  }

  private updatePaymentAccess(selectedProperty: UserPropertyResultDto): void {
    if (!!selectedProperty && !!selectedProperty.communityId) {
      this.loadingVisible = true;
      const param = new BaseResidentParameterDto(
        selectedProperty.customerId,
        selectedProperty.communityId
      );
      RxjsUtils.safeSubscribe(
        this.userService.getUserActionItems(param),
        (actions: ActionItemConfigResultDto[]) => {
          this.userActions = actions;
          if (!this.quickLinksItem) {
            this.quickLinksItem = new UserDashboardItemResultDto();
          }
          this.buildQuickLinksItem(this.quickLinksItem);
          this.addTileEventListeners();
          this.loadingVisible = false;
        }
      );
    }
  }

  private getAnnouncementCount() {
    const param: AnnouncementsParametersDto = new AnnouncementsParametersDto();
    param.clearCache = true;
    this.isLoadingAnnouncement = true;
    RxjsUtils.safeSubscribe(
      this.userService.getAnnouncementCount(param),
      (announcementCount) => {
        this.announcementCount = announcementCount;
        if (announcementCount > 0) {
          this.fetchLatestAnnouncement();
        } else {
          this.latestAnnouncement = null;
          this.isLoadingAnnouncement = false;
        }
      },
      (error) => {
        this.errorService.displayError(error);
        this.isLoadingAnnouncement = false;
      }
    );
  }

  private fetchLatestAnnouncement(): void {
    const param: AnnouncementsParametersDto = new AnnouncementsParametersDto();
    param.clearCache = false;
    RxjsUtils.safeSubscribe(
      this.userService.getAnnouncements(param),
      (announcements) => {
        if (announcements && announcements.length > 0) {
          this.latestAnnouncement = announcements[0];
          this.latestAnnouncementPlainText = this.stripHtmlTags(
            this.latestAnnouncement.htmlContent
          );
        } else {
          this.latestAnnouncement = null;
          this.latestAnnouncementPlainText = '';
        }
        this.isLoadingAnnouncement = false;
      },
      (error) => {
        console.warn('Failed to fetch latest announcement:', error);
        this.latestAnnouncement = null;
        this.latestAnnouncementPlainText = '';
        this.isLoadingAnnouncement = false;
      }
    );
  }

  getAnnouncementFormattedDate(releaseDate: string | Date): string {
    const userTimezone = this.helperService.getUserTimezone();
    return moment.tz(releaseDate, userTimezone).format('MM/DD/YYYY');
  }

  private stripHtmlTags(htmlContent: string): string {
    if (!htmlContent) {
      return '';
    }
    // Create a temporary element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    // Get text content (strips all HTML tags, preserves emojis)
    let text = tempDiv.textContent || tempDiv.innerText || '';
    // Normalize whitespace (collapse multiple spaces/newlines into single space)
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  }

  onViewAllAnnouncementsClick(): void {
    this.announcements.show();
  }

  private buildActivityItems(): void {
    if (
      !!this.dashboardResult &&
      !!this.dashboardResult.activityItems &&
      !!this.dashboardResult.activityItems.length
    ) {
      const activities: { key: string; items: ActivityFeedResultDto[] }[] = [];
      const lastActivity: ActivityFeedResultDto =
        this.dashboardResult.activityItems.find(
          (rec) => rec.code === 'last-activity'
        );
      if (!!lastActivity) {
        const key = this.getRecentActivityLabel(lastActivity);
        if (!!key) {
          activities.push({
            key,
            items: [lastActivity]
          });
        }
      }

      // Filter activities to only show selected property
      const filtered: ActivityFeedResultDto[] =
        this.dashboardResult.activityItems.filter(
          (rec) =>
            rec.code !== 'last-activity' &&
            !!this.selectedProperty &&
            rec.customerId === this.selectedProperty.customerId &&
            rec.communityId === this.selectedProperty.communityId
        );

      if (!!filtered.length) {
        const sortedActivities = this.getSortedActivities(filtered);
        activities.push({
          key: this.selectedProperty.propertyAddress,
          items: sortedActivities
        });
      }
      this.activityItem = new UserDashboardItemResultDto();
      this.activityItem.title = 'Account Activity';
      this.activityItem.hasIcon = false;
      this.activityItem.activities = activities;
    }
  }

  private getRecentActivityLabel(activity: ActivityFeedResultDto): string {
    const rec = activity.details[0];
    if (!!rec.value) {
      const lastDateTime = moment
        .tz(rec.value, this.helperService.getUserTimezone())
        .format('MM/DD/YYYY hh:mm A');
      return `Last user activity on the portal was on ${lastDateTime}`;
    }
    return null;
  }

  private getSortedActivities(
    activities: ActivityFeedResultDto[]
  ): ActivityFeedResultDto[] {
    return activities.sort((a, b) => {
      return a.activityDate > b.activityDate
        ? -1
        : b.activityDate > a.activityDate
          ? 1
          : 0;
    });
  }

  private buildAccountBalanceItem(
    item: UserDashboardItemResultDto,
    unpaidDateItem?: UserDashboardItemResultDto
  ): void {
    item.title = 'Current Balance';
    item.value = '';
    item.isPastDue = false;
    item.pastDueMessage = '';
    item.isInCollections = false;
    item.collectionsMessage = '';
    item.collectionsContactInfo = '';

    let sum = 0;
    let hasCollectionsProperty = false;
    let collectionsMessageFromApi = '';
    if (!!item.details && item.details.length > 0) {
      item.details.forEach((rec) => {
        if (Utils.isTrue(rec.isNumber)) {
          sum += parseFloat(rec.value);
        } else {
          // Non-numeric value indicates collections property - value contains the full message
          hasCollectionsProperty = true;
          collectionsMessageFromApi = rec.value || '';
        }
      });

      // Format balance regardless of sign (positive = owes, negative = credit)
      item.value = this.helperService.getCurrencyStringFormat(sum);
    }

    if (Utils.isNullOrEmpty(item.value)) {
      item.value = '$0.00';
    }

    // Check if in collections (property has non-numeric value indicating collections)
    if (hasCollectionsProperty) {
      item.isInCollections = true;
      // Extract attorney name and phone from API message
      // Format: "Your account is in collections and is currently being handled by {name}. Please contact {name} at {phone}."
      const phoneMatch = collectionsMessageFromApi.match(
        /at\s+([\d\(\)\-\s]+)\.?$/i
      );
      const attorneyMatch =
        collectionsMessageFromApi.match(/handled by ([^.]+)\./i);
      const attorneyName = attorneyMatch ? attorneyMatch[1].trim() : 'Attorney';
      const phoneNumber = phoneMatch ? phoneMatch[1].trim() : '';

      item.collectionsMessage = `Your account is in collections and is currently being handled by ${attorneyName}.`;
      item.collectionsContactInfo = phoneNumber;
    }
    // Check if past due based on WARNING status (only if selected property has balance)
    else if (item.status === 'WARNING' && sum > 0) {
      item.isPastDue = true;
      // Calculate days past due from unpaid date
      let daysPastDue = 0;
      if (unpaidDateItem?.details?.length > 0) {
        const unpaidDateStr = unpaidDateItem.details[0]?.value;
        if (unpaidDateStr) {
          const unpaidDate = moment(unpaidDateStr);
          const today = moment();
          daysPastDue = today.diff(unpaidDate, 'days');
        }
      }
      item.pastDueMessage =
        daysPastDue > 0
          ? `Your payment is ${daysPastDue} day${daysPastDue === 1 ? '' : 's'} past due`
          : 'Your Payment is Due';
    }

    item.html = null;
    item.hasIcon = false;
  }

  private buildComplianceItem(item: UserDashboardItemResultDto): void {
    item.title = 'Open Violations';
    item.hasIcon = false;
    // Sum up the actual violation count from details (value field contains count per property)
    const violationCount =
      item.details?.reduce((sum, d) => sum + (parseInt(d.value, 10) || 0), 0) ??
      0;
    const hasViolations = violationCount > 0;
    item.compactMessage = hasViolations
      ? `You have ${violationCount} open violation${violationCount === 1 ? '' : 's'}`
      : 'Thank you for your compliance!';
    item.compactLinkText = 'View All Violations';
    item.compactLinkId = 'complianceLink';
    item.compactIsOk = !hasViolations;
    item.compactIsWarning = hasViolations;

    if (hasViolations && this.selectedProperty) {
      this.fetchViolationDetails(item, violationCount);
    }
  }

  private buildArchitecturalApplicationItem(
    item: UserDashboardItemResultDto
  ): void {
    item.title = 'Architectural Requests';
    item.hasIcon = false;
    // Sum up the actual request count from details (value field contains count per property)
    const pendingCount =
      item.details?.reduce((sum, d) => sum + (parseInt(d.value, 10) || 0), 0) ??
      0;
    const hasPending = pendingCount > 0;
    item.compactMessage = hasPending
      ? `You have ${pendingCount} open request${pendingCount === 1 ? '' : 's'}`
      : 'No pending requests';
    item.compactLinkText = 'View All Requests or Submit New';
    item.compactLinkId = 'architecturalApplication';
    item.compactIsOk = !hasPending;
    item.compactIsWarning = hasPending;

    // Fetch detailed architectural requests for the compact card (latest 2)
    if (hasPending && this.selectedProperty) {
      this.fetchArchitecturalRequestDetails(item, pendingCount);
    }
  }

  private buildDocumentsItem(): void {
    if (!this.selectedProperty) {
      return;
    }

    this.documentsItem = new UserDashboardItemResultDto();
    this.documentsItem.title = 'My Documents';
    this.documentsItem.code = 'my-doc';
    this.documentsItem.status = 'OK';
    this.documentsItem.hasIcon = false;
    this.documentsItem.compactMessage = 'Loading documents...';
    this.documentsItem.compactLinkText = 'View All Documents';
    this.documentsItem.compactLinkId = 'documentsLink';
    this.documentsItem.compactIsOk = true;
    this.documentsItem.compactIsWarning = false;

    this.fetchDocumentDetails(this.documentsItem);
  }

  private fetchDocumentDetails(item: UserDashboardItemResultDto): void {
    const baseParam = new BaseResidentParameterDto(
      this.selectedProperty.customerId,
      this.selectedProperty.communityId
    );

    RxjsUtils.safeSubscribe(
      this.documentService
        .getDocumentTree(baseParam)
        .pipe(takeUntil(this.cancelDocumentFetch$)),
      (treeResults) => {
        if (!treeResults || treeResults.length === 0) {
          item.compactMessage = 'No documents available';
          item.documents = [];
          return;
        }

        // Filter out nodes with (0) count
        const validNodes = treeResults.filter(
          (node) => !node.text.trim().endsWith('(0)')
        );

        // Find the root node (id === "0" and parentId === "0")
        const rootNode = validNodes.find(
          (node) => node.id === '0' && node.parentId === '0'
        );
        const rootId = rootNode ? rootNode.id : '0';

        // Build a map of top-level category names (direct children of root)
        // These are: Budgets, Community Info, Forms, Insurance, Legal, etc.
        const topLevelCategories: { [id: string]: string } = {};
        validNodes.forEach((node) => {
          if (node.parentId === rootId && node.id !== rootId) {
            topLevelCategories[node.id] = node.text
              .replace(/\s*-?\s*\(\d+\)\s*$/, '')
              .trim();
          }
        });

        // For each node, resolve its top-level category by walking up the tree
        const nodeToTopCategory: { [id: string]: string } = {};
        const resolveTopCategory = (nodeId: string): string => {
          if (nodeToTopCategory[nodeId]) {
            return nodeToTopCategory[nodeId];
          }
          if (topLevelCategories[nodeId]) {
            nodeToTopCategory[nodeId] = topLevelCategories[nodeId];
            return topLevelCategories[nodeId];
          }
          const found = validNodes.find((n) => n.id === nodeId);
          if (found && found.parentId && found.parentId !== rootId) {
            const result = resolveTopCategory(found.parentId);
            nodeToTopCategory[nodeId] = result;
            return result;
          }
          return '';
        };

        // Get leaf nodes that have actual documents (have a category set)
        const leafNodes = validNodes.filter(
          (node) =>
            node.category !== '' && node.category !== null && node.id !== rootId
        );

        if (leafNodes.length === 0) {
          item.compactMessage = 'No documents available';
          item.documents = [];
          return;
        }

        // Fetch documents from all leaf nodes to find the 3 most recent
        this.fetchRecentDocuments(item, leafNodes, resolveTopCategory);
      },
      () => {
        item.compactMessage = 'No documents available';
        item.documents = [];
      }
    );
  }

  private fetchRecentDocuments(
    item: UserDashboardItemResultDto,
    leafNodes: any[],
    resolveTopCategory: (nodeId: string) => string
  ): void {
    const requests = leafNodes.map((node) => {
      const docParams = new DocumentParamterDto(
        this.selectedProperty.customerId,
        this.selectedProperty.communityId,
        node.category,
        node.subcategory,
        parseInt(node.month, 10),
        parseInt(node.year, 10)
      );

      return this.documentService
        .getDocumentList(docParams)
        .pipe(catchError(() => of([])));
    });

    RxjsUtils.safeSubscribe(
      forkJoin(requests).pipe(takeUntil(this.cancelDocumentFetch$)),
      (results) => {
        const allDocuments: {
          type: string;
          effectiveDate: Date;
          fileName: string;
          fileUrl: string;
        }[] = [];

        results.forEach((documents, index) => {
          const node = leafNodes[index];
          const topCategory =
            resolveTopCategory(node.id) || node.category || '';
          documents.forEach((doc) => {
            allDocuments.push({
              type: topCategory,
              effectiveDate: doc.effectiveDate,
              fileName: doc.fileName,
              fileUrl: doc.fileUrl
            });
          });
        });

        this.finalizeDocumentsList(item, allDocuments);
      },
      () => {
        this.finalizeDocumentsList(item, []);
      }
    );
  }

  private finalizeDocumentsList(
    item: UserDashboardItemResultDto,
    allDocuments: {
      type: string;
      effectiveDate: Date;
      fileName: string;
      fileUrl: string;
    }[]
  ): void {
    if (allDocuments.length === 0) {
      item.compactMessage = 'No documents available';
      item.documents = [];
    } else {
      // Sort by effective date descending and take latest 3
      item.documents = allDocuments
        .sort(
          (a, b) =>
            new Date(b.effectiveDate).getTime() -
            new Date(a.effectiveDate).getTime()
        )
        .slice(0, 3);
      item.compactMessage = '';
    }

    // Reassign to trigger OnPush change detection in the child component
    this.documentsItem = Object.assign(new UserDashboardItemResultDto(), item);
    this.addTileEventListeners();
  }

  private fetchArchitecturalRequestDetails(
    item: UserDashboardItemResultDto,
    totalCount: number
  ): void {
    const params = new AccParameterDto(
      this.selectedProperty.customerId,
      this.selectedProperty.communityId
    );
    params.open = true;

    RxjsUtils.safeSubscribe(
      this.architecturalApplicationService.getAccList(params),
      (results) => {
        // Get the latest 2 requests, sorted by received date (most recent first)
        const sortedResults = results
          .sort(
            (a, b) =>
              new Date(b.receivedDate).getTime() -
              new Date(a.receivedDate).getTime()
          )
          .slice(0, 2);

        item.architecturalRequests = sortedResults.map((req) => ({
          category: req.category,
          requestedDate: req.receivedDate,
          status: req.status
        }));

        // Update the count to reflect total open requests
        item.value = totalCount.toString();

        // Reassign to trigger OnPush change detection in the child component
        this.architecturalItem = Object.assign(
          new UserDashboardItemResultDto(),
          item
        );
      }
    );
  }

  private fetchViolationDetails(
    item: UserDashboardItemResultDto,
    totalCount: number
  ): void {
    const params = new ViolationParameterDto(
      true,
      this.selectedProperty.customerId,
      this.selectedProperty.communityId
    );

    RxjsUtils.safeSubscribe(
      this.accountComplianceService.getAccountViolations(params),
      (results) => {
        // Get all open violations, sorted by date (most recent first)
        const sortedResults = results
          .filter((v) => v.isOpen)
          .sort(
            (a, b) =>
              new Date(b.openCloseDate).getTime() -
              new Date(a.openCloseDate).getTime()
          )
          .slice(0, 2);

        // Initialize violations without notice info
        item.violations = sortedResults.map((violation) => ({
          category: violation.category,
          date: violation.openCloseDate,
          status: violation.status,
          noticeUrl: '',
          noticeFileName: ''
        }));

        item.value = totalCount.toString();

        // Reassign to trigger OnPush change detection in the child component
        this.complianceItem = Object.assign(
          new UserDashboardItemResultDto(),
          item
        );

        // For each violation, fetch its history to get the latest notice
        sortedResults.forEach((violation, index) => {
          if (violation.violationId) {
            this.fetchLatestNoticeForViolation(
              this.complianceItem,
              index,
              violation.violationId
            );
          }
        });
      }
    );
  }

  private fetchLatestNoticeForViolation(
    item: UserDashboardItemResultDto,
    index: number,
    violationId: string
  ): void {
    const historyParams = new ViolationHistoryParameterDto(
      this.selectedProperty.customerId,
      this.selectedProperty.communityId
    );
    historyParams.violationId = violationId;

    RxjsUtils.safeSubscribe(
      this.accountComplianceService.getAccountViolationDetails(historyParams),
      (historyResults) => {
        // Find the most recent history record that has a notice file
        // History is already sorted by inspectDate descending from the API
        const latestNotice = historyResults.find(
          (h) => h.noticeUrl && h.noticeFileName
        );

        if (latestNotice && item.violations[index]) {
          item.violations[index].noticeUrl = latestNotice.noticeUrl;
          item.violations[index].noticeFileName = latestNotice.noticeFileName;

          // Reassign to trigger OnPush change detection in the child component
          this.complianceItem = Object.assign(
            new UserDashboardItemResultDto(),
            item
          );
        }
      }
    );
  }

  private buildAnnouncementsItem(item: UserDashboardItemResultDto): void {
    item.code = 'announcement';
    item.title = 'Announcements';
    item.status = 'INFO';
    item.html = `There are no new announcements`;
    if (this.announcementCount == 0) {
      item.html = `<a id="announcement" class="dx-theme-accent-as-text-color">There are no new announcements</a>`;
    } else if (this.announcementCount == 1) {
      item.html = `There is a new  <a id="announcement" class="cc-clickable-link cc-hyper-link dx-theme-accent-as-text-color">announcement</a>`;
    } else if (this.announcementCount > 1) {
      item.html = `There are ${this.announcementCount} new <a id="announcement" class="cc-clickable-link cc-hyper-link dx-theme-accent-as-text-color">announcements</a>`;
    }
  }

  private buildQuickLinksItem(item: UserDashboardItemResultDto): void {
    const getQuickLink = (id: string, text: string, code?: string): string => {
      if (this.userActions.find((r) => r.item === code && r.enable === true)) {
        return `<div class="message-line">
                    <a id="${id}" class="link-text cc-clickable-link cc-hyper-link dx-theme-accent-as-text-color">${text}</a>
                </div>`;
      }
      return null;
    };

    const quickLinks = [
      getQuickLink(
        '/accounts/set-recurring-payment',
        'Set up Recurring Payments',
        'set-recurring-payment'
      ),
      getQuickLink(
        '/accounts/contact-information',
        'Update Contact Information',
        'account-info'
      ),
      getQuickLink(
        '/accounts/owner-information',
        'Update Mailing Information',
        'account-info'
      )
    ];

    item.title = `Quick Links`;
    item.html = quickLinks.filter((link) => !!link).join(` `);
    item.hasIcon = false;
  }

  private buildEmptyItem(item: UserDashboardItemResultDto): void {
    item.title = `Message`;
    item.status = 'HIGH-ALERT';
    item.html = `There is no property selected!`;
    if (this.userService.isInternal()) {
      item.html += `<br/> Impersonate an account to continue.`;
    }
  }

  private addTileEventListeners() {
    setTimeout(() => {
      const announcement = document.getElementById('announcement');
      if (!!announcement) {
        announcement.addEventListener('click', (event: Event) => {
          this.announcements.show();
        });
      }

      const complianceLink = document.getElementById('complianceLink');
      if (!!complianceLink) {
        complianceLink.addEventListener('click', () => {
          this.navigateToLink('compliance');
        });
      }

      const architecturalLink = document.getElementById(
        'architecturalApplication'
      );
      if (!!architecturalLink) {
        architecturalLink.addEventListener('click', () => {
          this.navigateToLink('architectural-application');
        });
      }

      const documentsLink = document.getElementById('documentsLink');
      if (!!documentsLink) {
        documentsLink.addEventListener('click', () => {
          this.navigateToLink('my-doc');
        });
      }

      const accountStatementLink = document.getElementById(
        'view-account-statement'
      );
      if (!!accountStatementLink) {
        accountStatementLink.addEventListener('click', () => {
          this.navigateToLink('account-statement');
        });
      }

      const domElementIds = [
        '/accounts/one-time-payment',
        '/accounts/set-recurring-payment',
        '/accounts/contact-information',
        '/accounts/owner-information',
        '/accounts/documents',
        'my-doc_legal.general',
        'my-doc_policies',
        'my-doc_mailings',
        'online-help'
      ];
      const clickHandler = (e) => {
        const elementId = e.target.id;
        if (elementId.indexOf('my-doc') !== -1) {
          const tokens = elementId.split('_');
          this.navigateToLink(tokens[0], null, tokens[1]);
        } else if (elementId.indexOf('online-help') !== -1) {
          const helpLink = this.getOnlineHelpLink(this.selectedProperty);
          this.ciraHelpService.showHelp(helpLink);
        } else {
          this.navigateToLink(elementId);
        }
      };
      for (let i = 0; i < domElementIds.length; i++) {
        const elementId = domElementIds[i];
        const element = document.getElementById(elementId);
        if (!!element) {
          element.onclick = clickHandler.bind(this);
        }
      }
    }, 1000);
  }

  private updateScreenSize(): void {
    const newIsSmallScreen = this.screenService.isXSmallScreen();
    const newIsLargeScreen = !this.screenService.isSmallScreen();

    // Only update if screen size category actually changed
    if (
      this.isSmallScreen !== newIsSmallScreen ||
      this.isLargeScreen !== newIsLargeScreen
    ) {
      this.isSmallScreen = newIsSmallScreen;
      this.isLargeScreen = newIsLargeScreen;
    }
  }

  private updateHelpLink(selectedProperty: UserPropertyResultDto): void {
    if (!!selectedProperty && !!selectedProperty.communityId) {
      this.helpLink = this.ciraHelpService.getMyDashboardHelpLink(
        selectedProperty.communityId
      );
    } else {
      this.helpLink = this.ciraHelpService.getMyDashboardHelpLink(null);
    }
  }

  private getOnlineHelpLink(selectedProperty: UserPropertyResultDto): string {
    let helpLink = this.ciraHelpService.getSideNavHelpLink(null);
    if (!!selectedProperty && !!selectedProperty.communityId) {
      helpLink = this.ciraHelpService.getSideNavHelpLink(
        selectedProperty.communityId
      );
    }
    return helpLink;
  }
}
